import React from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Box, Typography, Avatar, Chip, Divider, Paper, Stack, useTheme
} from '@mui/material';
import {
  Print as PrintIcon, Close as CloseIcon, Badge as BadgeIcon,
  Phone as PhoneIcon, Email as EmailIcon, LocationOn as LocationIcon,
  School as SchoolIcon, ContactPhone as EmergencyIcon
} from '@mui/icons-material';
import { QRCodeSVG } from 'qrcode.react';
import { Student } from '../types/academy';
import { useLanguage } from '../../Commun/contexts/LanguageContext';
import { useAuth } from '../../Commun/contexts/AuthContext';
import { useSystemParameters } from '../../Commun/contexts/SystemParametersContext';

interface StudentCardModalProps {
  open: boolean;
  onClose: () => void;
  student: Student | null;
}

export const StudentCardModal: React.FC<StudentCardModalProps> = ({
  open,
  onClose,
  student
}) => {
  const { t, language, dir } = useLanguage();
  const isRtl = dir === 'rtl';
  const theme = useTheme();
  const { user } = useAuth();
  const { getParam } = useSystemParameters();

  if (!student) return null;

  const agencyName = user?.dosLib || user?.agency || getParam('DOS_LIB') || getParam('Agency_Name') || 'BLOBTIC ACADEMY';
  const agencyPhone = user?.dosTelph || getParam('DOS_TELPH') || getParam('Agency_Phone') || '';
  const agencyEmail = user?.dosEmail || getParam('DOS_EMAIL') || getParam('Agency_Email') || '';
  const agencyAddress = user?.dosAdr1 || getParam('DOS_ADR1') || getParam('Agency_Address') || '';
  const agencyLogo = user?.dosLogo;

  // The QR code value contains the formatted student unique identifier
  const studentUid = student.uid || student.STD_UIDF || (student.id != null ? String(student.id) : '');
  const qrCodeValue = `BLO-STD-${studentUid}`;
  const studentCode = student.code || student.STD_CODE || `STD-${String(student.id).padStart(4, '0')}`;

  const handlePrint = () => {
    const cardEl = document.getElementById('student-id-card-printable');
    if (!cardEl) {
      window.print();
      return;
    }

    const styles = Array.from(document.querySelectorAll('link[rel="stylesheet"], style'))
      .filter(el => {
        if (el.tagName.toLowerCase() === 'link') return true;
        if (el.hasAttribute('data-emotion')) return true;
        const txt = el.textContent || '';
        // Completely exclude print stylesheets or hiding rules from other open tabs
        return !txt.includes('@media print') && !txt.includes('visibility: hidden') && !txt.includes('#printable');
      })
      .map(el => el.outerHTML)
      .join('\n');

    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.left = '-10000px';
    iframe.style.top = '-10000px';
    iframe.style.width = '210mm';
    iframe.style.height = '297mm';
    iframe.style.border = 'none';
    iframe.style.zIndex = '-9999';
    // Note: Do not use opacity: 0, which causes Chromium print compositor to render transparent blank pages
    document.body.appendChild(iframe);

    const doc = iframe.contentWindow?.document;
    if (!doc) return;

    doc.open();
    doc.write(`
      <!DOCTYPE html>
      <html dir="${isRtl ? 'rtl' : 'ltr'}" lang="${language}">
      <head>
        <title>${student.first_name || ''} ${student.last_name || ''} - ${language === 'ar' ? 'شارة التلميذ' : language === 'en' ? 'Student Badge' : "Badge d'apprenant"}</title>
        ${styles}
        <style>
          @page {
            size: A4 portrait;
            margin: 15mm;
          }
          * {
            box-sizing: border-box;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
          }
          html, body {
            margin: 0 !important;
            padding: 0 !important;
            background: #ffffff !important;
            width: 100% !important;
            height: auto !important;
            visibility: visible !important;
            font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
          }
          body, body * {
            visibility: visible !important;
          }
          .badge-print-wrapper {
            display: flex !important;
            justify-content: center !important;
            align-items: flex-start !important;
            width: 100% !important;
            padding-top: 25mm !important;
            visibility: visible !important;
          }
          #student-id-card-printable {
            box-shadow: none !important;
            border: 1px dashed #94a3b8 !important;
            page-break-inside: avoid !important;
            break-inside: avoid !important;
            margin: 0 auto !important;
            visibility: visible !important;
          }
          #student-id-card-printable * {
            visibility: visible !important;
          }
          .badge-photo-avatar,
          .badge-photo-avatar img {
            width: 84px !important;
            height: 84px !important;
            min-width: 84px !important;
            min-height: 84px !important;
            max-width: 84px !important;
            max-height: 84px !important;
            flex-shrink: 0 !important;
            object-fit: cover !important;
            border-radius: 50% !important;
          }
        </style>
      </head>
      <body>
        <div class="badge-print-wrapper">
          ${cardEl.outerHTML}
        </div>
      </body>
      </html>
    `);
    doc.close();

    setTimeout(() => {
      try {
        iframe.contentWindow?.focus();
        iframe.contentWindow?.print();
      } catch (err) {
        console.error('Print iframe error:', err);
      } finally {
        setTimeout(() => {
          if (document.body.contains(iframe)) {
            document.body.removeChild(iframe);
          }
        }, 2000);
      }
    }, 400);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden'
        }
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <BadgeIcon color="primary" />
          <Typography variant="h6" fontWeight={700}>
            {t('academy.students.print_card') || 'Student Badge'}
          </Typography>
        </Box>
        <Chip
          label={student.category === 'junior' ? `${t('academy.students.form.junior') || 'Junior'}` : `${t('academy.students.form.adult') || 'Adulte'}`}
          color="primary"
          size="small"
          variant="outlined"
          sx={{ fontWeight: 600 }}
        />
      </DialogTitle>

      <DialogContent sx={{ py: 2, display: 'flex', justifyContent: 'center' }}>

        {/* Printable ID Card Container (CR80 portrait badge style) */}
        <Paper
          id="student-id-card-printable"
          elevation={4}
          sx={{
            width: '320px',
            minHeight: '480px',
            borderRadius: '16px',
            overflow: 'hidden',
            bgcolor: '#ffffff',
            color: '#1e293b',
            display: 'flex',
            flexDirection: 'column',
            border: '1px solid',
            borderColor: 'divider',
            position: 'relative',
            boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)'
          }}
        >
          {/* Top Lanyard Slot Graphic */}
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1, pb: 0.5, bgcolor: theme.palette.primary.main }}>
            <Box sx={{ width: 44, height: 6, borderRadius: 3, bgcolor: 'rgba(255, 255, 255, 0.4)' }} />
          </Box>

          {/* Header Banner with Agency Branding */}
          <Box
            sx={{
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              color: '#ffffff',
              px: 2,
              py: 1.5,
              display: 'flex',
              alignItems: 'center',
              gap: 1.5
            }}
          >
            {agencyLogo ? (
              <Box
                component="img"
                src={agencyLogo}
                alt="Logo"
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: '8px',
                  bgcolor: '#ffffff',
                  p: 0.5,
                  objectFit: 'contain'
                }}
              />
            ) : (
              <Avatar sx={{ width: 44, height: 44, bgcolor: 'rgba(255,255,255,0.2)', color: '#fff' }}>
                <SchoolIcon />
              </Avatar>
            )}
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="subtitle2" fontWeight={800} noWrap sx={{ letterSpacing: 0.5, textTransform: 'uppercase', fontSize: '0.85rem' }}>
                {agencyName}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.9, fontSize: '0.68rem', display: 'block', fontWeight: 700 }} noWrap>
                {t('academy.students.card_subtitle') || (language === 'ar' ? 'شارة التلميذ' : language === 'en' ? 'Student Badge' : "Badge d'apprenant")}
              </Typography>
            </Box>
          </Box>

          {/* Student Photo & Identity Section */}
          <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', flex: 1 }}>
            <Avatar
              className="badge-photo-avatar"
              src={student.photo_base64}
              sx={{
                width: 84,
                height: 84,
                flexShrink: 0,
                mb: 1.2,
                border: `3px solid ${theme.palette.primary.main}`,
                boxShadow: '0 4px 10px rgba(0,0,0,0.15)',
                fontSize: '1.8rem',
                fontWeight: 700,
                bgcolor: theme.palette.primary.light,
                color: theme.palette.primary.contrastText,
                '& img': {
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }
              }}
            >
              {!student.photo_base64 && `${student.first_name?.[0] || ''}${student.last_name?.[0] || ''}`}
            </Avatar>

            <Typography variant="h6" fontWeight={800} sx={{ color: '#0f172a', lineHeight: 1.2, mb: 0.3 }}>
              {student.first_name} {student.last_name}
            </Typography>

            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase', mb: 1.2 }}>
              {studentCode}
            </Typography>

            {/* QR Code Container with High-Contrast White Background */}
            <Box
              sx={{
                p: 1,
                bgcolor: '#ffffff',
                border: '2px dashed',
                borderColor: theme.palette.primary.main,
                borderRadius: '12px',
                display: 'inline-flex',
                boxShadow: 'inset 0 0 4px rgba(0,0,0,0.05)',
                mb: 1.5
              }}
            >
              <QRCodeSVG
                value={qrCodeValue}
                size={115}
                level="H"
                includeMargin={true}
              />
            </Box>

            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem', fontWeight: 600, mb: 1 }}>
              {qrCodeValue}
            </Typography>

            {/* Student metadata list */}
            <Stack spacing={0.4} sx={{ width: '100%', px: 1, textAlign: isRtl ? 'right' : 'left', fontSize: '0.75rem' }}>
              {student.date_of_birth && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="caption" color="text.secondary">
                    {language === 'ar' ? 'تاريخ الميلاد :' : language === 'en' ? 'Date of birth :' : 'Né(e) le :'}
                  </Typography>
                  <Typography variant="caption" fontWeight={600}>{student.date_of_birth}</Typography>
                </Box>
              )}
              {student.emergency_contact && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                    <EmergencyIcon sx={{ fontSize: 13, color: 'error.main' }} /> {language === 'ar' ? 'طوارئ :' : language === 'en' ? 'Emergency :' : 'Urgence :'}
                  </Typography>
                  <Typography variant="caption" fontWeight={600} color="error.main">{student.emergency_contact}</Typography>
                </Box>
              )}
            </Stack>
          </Box>

          <Divider sx={{ borderStyle: 'dashed' }} />

          {/* Agency Contact Footer */}
          <Box sx={{ px: 1.5, py: 1, bgcolor: '#f8fafc', fontSize: '0.68rem', color: '#64748b' }}>
            {agencyAddress && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.3 }}>
                <LocationIcon sx={{ fontSize: 12, color: theme.palette.primary.main }} />
                <Typography variant="caption" sx={{ fontSize: '0.68rem' }} noWrap>{agencyAddress}</Typography>
              </Box>
            )}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              {agencyPhone && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <PhoneIcon sx={{ fontSize: 12, color: theme.palette.primary.main }} />
                  <Typography variant="caption" sx={{ fontSize: '0.68rem' }}>{agencyPhone}</Typography>
                </Box>
              )}
              {agencyEmail && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <EmailIcon sx={{ fontSize: 12, color: theme.palette.primary.main }} />
                  <Typography variant="caption" sx={{ fontSize: '0.68rem' }} noWrap>{agencyEmail}</Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Paper>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, justifyContent: 'space-between' }}>
        <Button onClick={onClose} color="inherit" startIcon={<CloseIcon />}>
          {t('btn.close') || 'Fermer'}
        </Button>
        <Button
          variant="contained"
          color="primary"
          startIcon={<PrintIcon />}
          onClick={handlePrint}
          sx={{ borderRadius: 2, px: 3 }}
        >
          {t('academy.students.print_badge') || 'Imprimer le Badge'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
